/* eslint-disable react-hooks/exhaustive-deps */
import React, { createRef, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import SearchGridWrapper from 'app/layout/pages/SearchGridWrapper';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { gridStateActions } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.redux';
import { revalidateChipSelection } from '@AMIEWEB/GlobalSearch/helper';
import { selectFilters, selectGridState } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.selectors';
import { RootState } from 'types';
import { userDevicePreferenceActions } from 'oidc/UserDevicePreference/userPreference.redux';
import { selectUserPreference } from 'oidc/UserDevicePreference/userPreference.selectors';
import { defaultFilterValues } from './filterUtils';
import { PlacementPayPackagesFilters } from './PlacementPayPackagesFilters';
import { PlacementPayPackagesChips } from './PlacementPayPackagesChips';
import { PlacementPayPackagesGrid } from './PlacementPayPackagesGrid';
import { usePlacementPayPackagesFetch } from './usePlacementPayPackagesFetch';

export const PlacementPayPackagesWrapper = ({ orderId }: { orderId: number }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const fetchPlacementPayPackages = usePlacementPayPackagesFetch({ orderId });
  const filterPreferencesId = `placement-pay-packages-filter-${orderId}`;

  const formMethods = useForm({ defaultValues: defaultFilterValues });
  const { handleSubmit, reset } = formMethods;

  const gridState = useSelector(selectGridState);
  const appliedFilterValues = useSelector(selectFilters);

  const filterPreference = useSelector((state: RootState) => {
    return state.userPreferenceData.userGridPreferences.find(item => item.id === filterPreferencesId);
  });
  const { loaded: preferencesLoaded } = useSelector(selectUserPreference);

  const hiddenSubmitButton = createRef<HTMLButtonElement>();

  const onApplyFilter = (filterValues, newSelectedChipFilters?) => {
    dispatch(gridStateActions.setPageNumber(1));
    const selectedChipFilters =
      newSelectedChipFilters ||
      revalidateChipSelection(filterValues, gridState.filterAttributes?.selectedChipFilters || []);
    dispatch(
      gridStateActions.setFilterAttributes({
        ...gridState.filterAttributes,
        filterObject: Object.entries(filterValues ?? defaultFilterValues),
        selectedChipFilters,
      }),
    );
    fetchPlacementPayPackages({
      pageNumber: 1,
      filters: filterValues ?? defaultFilterValues,
      selectedChipFilters,
    });
  };

  const onApplyFilterAndSave = (filterValues, newSelectedChipFilters?) => {
    const preferenceData = {
      id: filterPreferencesId,
      value: {
        filterValues: filterValues,
        newSelectedChipFilters: newSelectedChipFilters ?? appliedFilterValues?.selectedChipFilters,
      },
    };
    onApplyFilter(preferenceData.value.filterValues, newSelectedChipFilters);
    dispatch(userDevicePreferenceActions.saveUserGridPreferenceAction(preferenceData));
  };

  const saveFilterPreferences = (filterValues, newSelectedChipFilters?) => {
    onApplyFilterAndSave(filterValues, newSelectedChipFilters);
  };

  useEffect(() => {
    if (preferencesLoaded) {
      const payload = filterPreference?.value ?? { filterValues: defaultFilterValues };
      reset(payload?.filterValues);
      onApplyFilter(payload?.filterValues, payload?.newSelectedChipFilters);
    }
  }, [orderId, filterPreference, preferencesLoaded]);

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(filter => onApplyFilterAndSave(filter))}>
        <SearchGridWrapper
          filters={
            <PlacementPayPackagesFilters
              orderId={orderId}
              saveFilterPreferences={saveFilterPreferences}
              onRefresh={() => fetchPlacementPayPackages({ pageNumber: 1, pageSize: 20 })}
            />
          }
          table={<PlacementPayPackagesGrid orderId={orderId} />}
          chips={<PlacementPayPackagesChips onApplyFilter={onApplyFilterAndSave} />}
          tableMinHeight={430}
          loading={false}
        />
        <button ref={hiddenSubmitButton} style={{ display: 'none' }} type="submit" />
      </form>
    </FormProvider>
  );
};
